import { AnimatedFeedbackParams, Coordinate, Entity, randomIntBetween, TileSize } from "game-common/models"
import { Callback, degrees_to_radians } from "game-common/util"
import { AnimatedSprite, BitmapText, Container, Graphics, Loader, Rectangle, Sprite, Text, Texture } from "pixi.js"
import { logger } from "../../../../logger"

import { SpriteMeta } from "../entity_rendering_manager"
import { WorldOverlayItem } from "../world_overlay_surface"
import { BaseRenderable } from "./base_renderable"
import { EntityHighlight, HighlightFrame } from "./entity_highlight"
import { clickAreaFor } from "./click_area"
import { textureContentBounds } from "./texture_content_bounds"
import { TileTextureExtractor } from "./tile_texture_extractor"
import { TextRenderer } from "../text/text_renderer"
import { AnimatedFeedback } from "../effect/animated_feedback"
import { shatterSprite } from "../effects/sprite_shatter"

export class BasicSprite extends BaseRenderable {
    currentColor: number
    animation: AnimatedSprite
    sprite: Sprite
    // SignSprite labels with a BitmapText (bitmap fonts are not Text subclasses), and only
    // `.text`/`.visible`/position are ever touched here, which both types provide.
    labelText: Text | BitmapText

    container: Container
    rotationInterval: any
    private originalColor: number
    private worldOverlayItem: WorldOverlayItem
    private entityHighlight: EntityHighlight
    private clickArea: BaseRenderable
    private _scale: number
    private supportLabel: boolean
    private spriteId: string
    private scaleValue: number
    private spriteMeta: SpriteMeta
    private animatedFeedback: AnimatedFeedback

    // `withClickArea` gives this sprite its own hover/hit surface sized to the rendered art (see
    // updateClickAreaBounds) instead of hit-testing the whole renderable. Entities the player can
    // point at -- the interactable markers -- opt in; pure decoration doesn't.
    constructor(spriteMeta: SpriteMeta, withClickArea?: boolean) {
        super()

        this.spriteMeta = spriteMeta
        const {
            color = 0xffffff,
            name,
            scale = 1.0,
            alpha = 1.0,
            animated,
            animationSpeed = 0.167,
            rotating,
            looping = true,
            rotation,
            onLoopDone,
            supportLabel,
        } = spriteMeta

        this.spriteId = name
        this.supportLabel = supportLabel
        this.scaleValue = scale

        if (withClickArea) {
            this.clickArea = new BaseRenderable()

            if (this.clickArea.parent) {
                this.clickArea.parent.removeChild(this.parentArea)
            }
            // Hover/hit region is sized to the actual rendered art in updateClickAreaBounds()
            // (called below, after the sprite is built). We deliberately draw NOTHING here and
            // rely on pixi's `hitArea` Rectangle instead of a filled Graphics — see that method.
            this.addChild(this.clickArea)
        }
        this.container = new Graphics()
        this.addChild(this.container)

        this.originalColor = color
        this.currentColor = color

        const loader = Loader.shared

        let resource = loader.resources["humanoid"]

        // A sheet that failed to load leaves the resource present but unparsed, so `.spritesheet`
        // is undefined and reading `.animations` off it throws -- inside a constructor, on the
        // entity-creation path, which takes out every renderable in the room and leaves a world
        // with terrain and no characters in it. One unreachable file should cost one sprite, not
        // all of them, so degrade the way lpc_sprite.setup() already does.
        const sheet = resource?.spritesheet
        const isAnimated = animated !== undefined ? animated : !!sheet?.animations[name]

        this._scale = scale

        // `animated: true` can be passed explicitly, so the frames still have to be there before
        // this branch dereferences them.
        if (isAnimated && sheet?.animations[name]) {
            this.animation = new AnimatedSprite(sheet.animations[name])
            this.animation.scale.set(0.35)

            // set speed, start playback and add it to the stage
            this.animation.animationSpeed = animationSpeed
            this.animation.tint = color
            this.animation.alpha = alpha
            this.animation.play()
            this.animation.scale.set(scale)
            this.container.addChild(this.animation)

            if (!looping) {
                this.animation.loop = false
                if (onLoopDone) {
                    this.animation.onComplete = onLoopDone
                }
            }
        } else {
            let tileIdToUse = name
            let texture
            if (name.includes(":")) {
                const [resourceName, tileId] = name.split(":")
                tileIdToUse = `${resourceName}-${tileId}`
                resource = Loader.shared.resources[resourceName]

                const tileset = spriteMeta?.tileMapMetaProvider?.getTilesetById(resourceName)

                if (tileset) {
                    const tilesetTileMeta = tileset.tiles[Number(tileId) - 1]
                    if (tilesetTileMeta) {
                        const compositeTileId = tilesetTileMeta.compositeTileId
                        if (compositeTileId) {
                            texture = TileTextureExtractor.instance.extract(
                                spriteMeta.tileMapMetaProvider,
                                tileset.tilesetId,
                                compositeTileId,
                            )
                        }
                    }
                }
            }

            if (!texture) {
                texture = resource?.textures[tileIdToUse]
            }

            if (texture) {
                this.sprite = new Sprite(texture)
                if (name.includes(":")) {
                    this.sprite.transform.position.x -= texture.width / 2
                    this.sprite.transform.position.y -= 14
                }
                this.sprite.tint = color
                this.sprite.scale.set(scale)
                this.container.addChild(this.sprite)
            }
        }

        this.updateClickAreaBounds()

        this.labelText = new TextRenderer().size("tiny").color(0x00ff00).render() as Text
        this.labelText.visible = false
        this.labelText.x = -this.container.width * 0.23
        this.labelText.y = -this.container.height * 0.7
        this.addChild(this.labelText)

        if (rotation !== undefined) {
            this.container.angle = rotation
        }
        if (rotating) {
            this.startRotation()
        }

        this.animatedFeedback = new AnimatedFeedback()
    }

    updateScale = (scale: number) => {
        if (this.animation) {
            this.animation.scale.set(scale)
        }
        if (this.sprite) {
            this.sprite.scale.set(scale)
        }
        this.updateClickAreaBounds()
    }
    startRotation = () => {
        let angle = 0
        this.rotationInterval = setInterval(() => {
            if (angle > 360) {
                angle = 0
            }
            this.container.angle = angle
            angle++
        }, 50)
    }

    updateRotating = (rotating: boolean) => {
        if (!rotating) {
            if (this.rotationInterval) {
                clearInterval(this.rotationInterval)
                this.container.angle = 0
            }
        } else {
            this.startRotation()
        }
    }

    getCurrentColor = () => this.currentColor

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color
        if (this.animation) {
            this.animation.tint = color
        }
        if (this.sprite) {
            this.sprite.tint = color
        }
    }

    updateName(name: string) {
        if (!this.supportLabel) {
            return
        }
        if (name) {
            this.labelText.text = name.replace(/ /g, "\n")
            this.labelText.visible = true
        } else {
            this.labelText.visible = false
        }
    }

    getClickSurface = (): BaseRenderable => {
        if (this.clickArea) {
            return this.clickArea
        }

        return super.getClickSurface()
    }

    // Size the hover/hit region to the actual rendered art instead of a fixed one-tile box.
    // Uses pixi's `hitArea` Rectangle: nothing is drawn (no batched geometry, no per-frame GPU
    // cost) and hit-testing short-circuits to a rectangle contains-check. This runs ONCE here at
    // construction, and again only on sprite/scale change (both change-gated) — never per frame —
    // so it stays cheap on low-end hardware.
    private updateClickAreaBounds = () => {
        if (!this.clickArea) {
            return
        }

        this.clickArea.x = 0
        this.clickArea.y = 0
        this.clickArea.hitArea = clickAreaFor(this.visibleArtBounds())
    }

    // What this sprite actually draws, in the renderable's own local space -- the region a pointer
    // should have to be inside.
    //
    // NOT simply the container's bounds: a composite tile texture is the bounding box of every tile
    // making up the object (see TileTextureExtractor), and for the ember campfire that box carries
    // ~32px of empty tile out to each side. Taking it at face value made the campfire react to a
    // pointer a whole tile away from the flame. Trim to the texture's opaque pixels where we can;
    // fall back to the container bounds when we can't (an animation, or no renderer to read back
    // from, as in tests).
    private visibleArtBounds = (): Rectangle | null => {
        const containerBounds = this.container.getLocalBounds()
        const fallback =
            containerBounds.width > 0 &&
            containerBounds.height > 0 &&
            isFinite(containerBounds.x) &&
            isFinite(containerBounds.y)
                ? containerBounds
                : null

        if (!this.sprite || this.animation) {
            return fallback
        }
        const content = textureContentBounds(this.sprite.texture, this.spriteId)
        if (!content) {
            return fallback
        }
        // content is in texture pixel space; the sprite's own position already carries the centering
        // shift applied to composite tiles, and its scale the render scale.
        return new Rectangle(
            this.sprite.position.x + content.x * this.sprite.scale.x,
            this.sprite.position.y + content.y * this.sprite.scale.y,
            content.width * this.sprite.scale.x,
            content.height * this.sprite.scale.y,
        )
    }

    // Reports false for a blank stand-in sprite: the V2 chest draws its real art in the TILEMAP and
    // keeps only an invisible 32x32 marker here, so there is no shape here to trace and the caller
    // outlines the map tiles instead. Anything with actual art (a door, an outpost, a dropped item)
    // traces itself.
    setHighlighted = (on: boolean): boolean => {
        if (!this.highlightFrame()) {
            return false
        }
        if (!this.entityHighlight) {
            this.entityHighlight = new EntityHighlight({
                artContainer: this.container,
                frame: this.highlightFrame,
            })
        }
        this.entityHighlight.set(on)
        return true
    }

    private highlightFrame = (): HighlightFrame | null => {
        // A blank stand-in sprite has nothing to outline; say so and take the ring instead.
        if (!this.spriteId || this.spriteId.startsWith("blank")) {
            return null
        }
        const art: any = this.animation ?? this.sprite
        const texture: Texture = this.animation
            ? (this.animation.textures[this.animation.currentFrame] as Texture)
            : this.sprite?.texture
        if (!art || !texture?.valid) {
            return null
        }
        return {
            texture,
            x: art.position.x,
            y: art.position.y,
            scaleX: art.scale.x,
            scaleY: art.scale.y,
        }
    }

    setWorldOverlayItem = (item: WorldOverlayItem) => {
        this.worldOverlayItem = item
    }

    fragment(_: Entity, callback: Callback<void>) {
        const surface = new Graphics()
        surface.scale.set(this._scale)
        surface.rotation = this.container.rotation
        this.parent.addChild(surface)

        const f: Texture = this.animation
            ? (this.animation.textures[this.animation.currentFrame] as any)
            : this.sprite?.texture

        const { width, height } = f._frame

        surface.x -= width / 2
        surface.y -= height / 2

        shatterSprite(surface, f, { onSpawned: () => callback?.(), cacheKey: this.spriteId })
    }

    updateSpriteId(spriteId: string) {
        if (!spriteId) {
            return
        }
        if (this.spriteId === spriteId) {
            // noop, it has changed already
            return
        }

        // todo ... this only really supports non-animated sprites

        if (this.sprite) {
            this.container.removeChild(this.sprite)
        }
        if (this.animation) {
            this.container.removeChild(this.animation)
        }

        this.spriteId = spriteId

        let resource = Loader.shared.resources["humanoid"]
        let tileIdToUse = spriteId
        if (spriteId.includes(":")) {
            const [resourceName, tileId] = spriteId.split(":")
            tileIdToUse = `${resourceName}-${tileId}`
            resource = Loader.shared.resources[resourceName]
        }

        if (resource) {
            // `resource` being present says only that the loader knows the url, not that the sheet
            // behind it parsed -- a failed or still-parsing sheet leaves `.spritesheet` (or its
            // `.animations`) undefined, and reading through it throws inside this constructor, on
            // the entity-creation path, taking out every renderable in the room. Same reason the
            // constructor above guards; this is the second door into the same trap.
            const isAnimated = !!resource.spritesheet?.animations?.[spriteId]
            if (isAnimated) {
                const {
                    color = 0xffffff,
                    scale = 1.0,
                    alpha = 1.0,
                    animationSpeed = 0.167,
                    looping = true,
                    onLoopDone,
                } = this.spriteMeta
                this.animation = new AnimatedSprite(resource.spritesheet.animations[spriteId])
                this.animation.scale.set(0.35)

                // set speed, start playback and add it to the stage
                this.animation.animationSpeed = animationSpeed
                this.animation.tint = color
                this.animation.alpha = alpha
                this.animation.play()
                this.animation.scale.set(scale)
                this.container.addChild(this.animation)

                if (!looping) {
                    this.animation.loop = false
                    if (onLoopDone) {
                        this.animation.onComplete = onLoopDone
                    }
                }
            } else {
                const texture = resource.textures?.[tileIdToUse]
                if (texture) {
                    this.sprite = new Sprite(texture)
                    this.sprite.tint = this.currentColor
                    this.sprite.scale.set(this.scaleValue)
                    this.container.addChild(this.sprite)
                }
            }
        }

        this.updateClickAreaBounds()
    }

    getYOffset() {
        if (this.spriteId.includes("-exact")) {
            return 0
        }

        return super.getYOffset()
    }

    // Floating feedback over this sprite. A running body's dust trail does NOT come through here --
    // see LpcComposite.feedback and FootDust: it is ground art, z-sorted in the world surface, and
    // EntityRenderingManager.feedback routes it away before it reaches a renderable.
    feedback(entity: Entity, image: string, label: string, params?: AnimatedFeedbackParams) {
        logger.debug({ tags: ["sprite", "feedback"] }, "Feedback")
        this.animatedFeedback.setup(
            0,
            this.worldOverlayItem.height / 2,
            image,
            label,
            this.worldOverlayItem,
            params,
            this.sprite as any,
        )
    }
}
